import { Helmet } from "react-helmet-async";

interface SEOProps {
  title: string;
  description?: string;
  url?: string;
  image?: string;
  type?: "website" | "article";
  publishedTime?: string;
  author?: string;
  section?: string;
  tags?: string[];
  canonicalUrl?: string;
  structuredData?: any;
  noindex?: boolean;
}

export function SEO({
  title,
  description = "Ecclesiato | Sistema de Gestão para Igrejas. Simplifique a administração da sua igreja, organize membros, finanças e eventos em um só lugar.",
  url,
  image = "/og-image.jpg",
  type = "website",
  publishedTime,
  author,
  section,
  tags,
  canonicalUrl,
  structuredData,
  noindex = false,
}: SEOProps) {
  const siteDomain = "https://ecclesiato.com.br";

  // If canonicalUrl is provided, use it as the base for other URL tags if url is not provided
  const effectiveUrl = url || canonicalUrl || "";
  let fullUrl = effectiveUrl.startsWith("http")
    ? effectiveUrl
    : `${siteDomain}${effectiveUrl.startsWith("/") ? "" : "/"}${effectiveUrl}`;

  // Remove trailing slash for consistency unless it's just the root
  if (fullUrl.endsWith("/") && fullUrl.length > siteDomain.length + 1) {
    fullUrl = fullUrl.slice(0, -1);
  }

  const fullImage = image.startsWith("http")
    ? image
    : `${siteDomain}${image.startsWith("/") ? "" : "/"}${image}`;

  const siteTitle = "Ecclesiato";
  // Evita duplicar a marca quando o título já a contém (ex: "Ecclesiato | ...")
  const finalTitle = title.includes(siteTitle)
    ? title
    : `${title} | ${siteTitle}`;

  // Structured Data (JSON-LD)
  const defaultStructuredData =
    type === "article"
      ? {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: title,
          image: fullImage,
          author: {
            "@type": "Person",
            name: author || "Equipe Ecclesiato",
          },
          publisher: {
            "@type": "Organization",
            name: "Ecclesiato",
            logo: {
              "@type": "ImageObject",
              url: `${siteDomain}/logo.png`,
            },
            url: siteDomain,
          },
          datePublished: publishedTime,
          description: description,
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": fullUrl || siteDomain,
          },
        }
      : {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Ecclesiato",
          alternateName: ["Ecclesiato Gestão de Igrejas", "Sistema Ecclesiato"],
          url: siteDomain,
          description: description,
          publisher: {
            "@type": "Organization",
            name: "Ecclesiato",
            logo: {
              "@type": "ImageObject",
              url: `${siteDomain}/logo.png`,
            },
          },
        };

  const jsonLdData = structuredData || defaultStructuredData;

  const finalCanonical = canonicalUrl || fullUrl || siteDomain;

  return (
    <Helmet>
      {/* Standard Metadata */}
      <title>{finalTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={finalCanonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta name="application-name" content="Ecclesiato" />
      <meta name="apple-mobile-web-app-title" content="Ecclesiato" />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl || siteDomain} />
      <meta property="og:site_name" content="Ecclesiato" />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:url" content={fullUrl || siteDomain} />
      <meta name="twitter:site" content="@ecclesiato" />

      {/* Article Specific Tags */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && section && (
        <meta property="article:section" content={section} />
      )}
      {type === "article" &&
        tags &&
        tags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

      {/* Schema.org Structured Data */}
      <script type="application/ld+json">{JSON.stringify(jsonLdData)}</script>
    </Helmet>
  );
}
